/**
 * 어드민 인증 상태 — Pinia.
 */
import { defineStore } from 'pinia';
import type { AuthUser } from '@nojira/shared';
import * as authApi from '@/api/auth';
import type { LoginInput } from '@nojira/shared/schemas/auth';

interface AuthState {
  user: AuthUser | null;
  initialized: boolean;
  loading: boolean;
}

/** super admin 롤 — 페이지 권한과 무관하게 전부 노출(잠금 방지 break-glass). */
const SUPER_ADMIN_ROLE = 'Super Admin';

/** admin 콘솔 스태프 역할 — 이 중 하나라도 있으면 agent_admin 제한 대상이 아니다. */
const STAFF_ROLES = [
  'Super Admin', 'Accounting Manager', 'Accounting User',
  'Accounting Supporter', 'Sales Manager', 'Sales Assist', 'Legal Manager', 'Legal User', 'Attorney',
  'relocation_admin', 'Commission Manager', 'Commission User', 'Campaign User',
];

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    initialized: false,
    loading: false,
  }),

  getters: {
    isAuthenticated: (state): boolean => state.user !== null,
    /** super admin — 라우트 role 목록과 페이지 권한을 모두 통과한다. */
    isSuperAdmin: (state): boolean =>
      state.user?.roles.some((r) => r.name === SUPER_ADMIN_ROLE) ?? false,
    hasRole:
      (state) =>
      (names: string[]): boolean =>
        state.user?.roles.some((r) => names.includes(r.name)) ?? false,
    /**
     * agent_admin 만 보유(다른 스태프 역할 없음) → 제한적 admin 접근(blog/users/applicants).
     * 다른 스태프 역할도 있으면 그 역할대로 동작(제한 아님).
     */
    isRestrictedAgentAdmin: (state): boolean => {
      const names = state.user?.roles.map((r) => r.name) ?? [];
      return names.includes('agent_admin') && !names.some((n) => STAFF_ROLES.includes(n));
    },
    /**
     * 바깥 법무법인 변호사 — Attorney 만 있고 다른 스태프 역할이 없는 계정.
     *
     * 서버의 attorneyScopeUserId(middleware/auth.ts)와 같은 기준이어야 한다.
     * 그쪽이 자료를 자기 캠페인으로 좁히고, 이쪽은 어느 화면으로 보낼지를 정한다.
     *
     * STAFF_ROLES 에 'Attorney' 가 들어 있어 그대로 쓰면 항상 false 다 —
     * 자기 자신을 뺀 나머지로 판정한다.
     */
    isExternalAttorney: (state): boolean => {
      const names = state.user?.roles.map((r) => r.name) ?? [];
      if (!names.includes('Attorney')) return false;
      return !names.some((n) => n !== 'Attorney' && STAFF_ROLES.includes(n));
    },
    /**
     * 페이지 권한(pageKey=레거시 pages.path)로 노출 여부 판정.
     * - 매핑 없는(신규) 페이지는 항상 노출(신규 기능 숨김 방지).
     * - 지정 super admin(it@)은 전부 노출 — 잠금 방지 안전밸브(role 무관).
     * - 그 외는 pagePermissions 의 canShow 로 판정(역할기반 화이트리스트).
     */
    canShowPage:
      (state) =>
      (pageKey: string | undefined): boolean => {
        // 매핑 없는 경로는 막는다. 예전에는 통과시켰는데(신규 화면 잠금 방지),
        // meta.roles 를 걷어낸 뒤로는 그 관대함이 곧 무권한 통과가 된다 —
        // 새 화면은 pagePermissionMap 과 pages 에 등록해야 열린다.
        if (!pageKey) return false;
        if (state.user?.roles.some((r) => r.name === SUPER_ADMIN_ROLE)) return true;
        return state.user?.pagePermissions?.some((p) => p.pageKey === pageKey && p.canShow) ?? false;
      },
    /**
     * 페이지 수정 권한(canModify) — 액션 버튼 노출 판정에 사용.
     * - 매핑 없는(신규) 페이지는 true(버튼 과다 숨김 방지).
     * - super admin 은 전부 true.
     * - 그 외는 pagePermissions 의 canModify 로 판정(데이터 기반).
     */
    /**
     * 페이지 읽기 권한(canRead||canModify) — 화면 안의 '읽기 전용 패널' 노출 판정.
     *
     * canShowPage 와 갈라 둔 이유: can_show 는 "좌측 메뉴에 그 화면을 띄우고 직접
     * 진입시킬 것인가" 이고, 읽기 패널의 기준은 그것보다 낮다. 실제로 회계 역할은
     * 케이스 화면 자체는 열 일이 없어 can_show=false 인데, 신청인 드로어의
     * 타임라인은 봐야 한다 — can_show 로 판정하면 API 가 200 인데 화면만 비는
     * 상태가 된다(0440).
     */
    canReadPage:
      (state) =>
      (pageKey: string | undefined): boolean => {
        if (!pageKey) return false;
        if (state.user?.roles.some((r) => r.name === SUPER_ADMIN_ROLE)) return true;
        return (
          state.user?.pagePermissions?.some(
            (p) => p.pageKey === pageKey && (p.canRead || p.canModify),
          ) ?? false
        );
      },
    canModifyPage:
      (state) =>
      (pageKey: string | undefined): boolean => {
        if (!pageKey) return true;
        if (state.user?.roles.some((r) => r.name === SUPER_ADMIN_ROLE)) return true;
        return (
          state.user?.pagePermissions?.some((p) => p.pageKey === pageKey && p.canModify) ?? false
        );
      },
    /**
     * Case 상태 변경(전이·spawn·close·큐 액션) 가능 여부 — 서버
     * requireCaseStatusEditor 와 같은 기준(admin.caseStatusChange 의 modify).
     * 역할 목록이 아니라 권한 데이터를 본다(0515) — 권한 화면에서 켜 준 역할은
     * 버튼도 함께 보여야 한다.
     */
    /**
     * 케이스 상세 TRANSITION 버튼 — caseStatusChange 또는 전환 전용 키(0525).
     * 전환 전용 키는 다른 상태 변경 동작(Close·다음 단계·경로 전환)을 열지 않는다.
     */
    canTransitionCase: (state): boolean => {
      if (state.user?.roles.some((r) => r.name === SUPER_ADMIN_ROLE)) return true;
      return (
        state.user?.pagePermissions?.some(
          (p) => (p.pageKey === 'admin.caseStatusChange' || p.pageKey === 'admin.caseTransition') && p.canModify,
        ) ?? false
      );
    },
    /**
     * Withdraw / Close 분류(철회 확인·Refund/Close Request·환불 금액·Hold) — 전용 키(0567)만.
     * caseStatusChange 는 재무도 가져(0515) 인정하지 않는다 — 서버 requireWithdrawClassify 와 같다.
     */
    canWithdrawClassify: (state): boolean => {
      if (state.user?.roles.some((r) => r.name === SUPER_ADMIN_ROLE)) return true;
      return (
        state.user?.pagePermissions?.some(
          (p) => p.pageKey === 'admin.withdrawClassify' && p.canModify,
        ) ?? false
      );
    },
    canEditCaseStatus: (state): boolean => {
      if (state.user?.roles.some((r) => r.name === SUPER_ADMIN_ROLE)) return true;
      return (
        state.user?.pagePermissions?.some(
          (p) => p.pageKey === 'admin.caseStatusChange' && p.canModify,
        ) ?? false
      );
    },
    /**
     * Employer › Credentials 열람 가능 여부 — backend requireEmployerCredentialViewer 와
     * 같은 목록. 그 응답에는 복호화된 비밀번호가 실리므로 캠페인 팀과 Super Admin 만.
     * 이 값이 false 면 탭 자체를 숨긴다 — 열어 봐야 403 이다.
     */
    canViewEmployerCredentials: (state): boolean =>
      state.user?.roles.some((r) => r.name === 'Campaign User' || r.name === SUPER_ADMIN_ROLE) ??
      false,
  },

  actions: {
    async initialize(): Promise<void> {
      if (this.initialized) return;
      this.initialized = true;
      this.loading = true;
      try {
        this.user = await authApi.fetchMe();
      } catch {
        // 세션 만료(401)나 일시적 네트워크 실패를 여기서 던지면 authGuard 의
        // `await auth.initialize()` 가 throw 하고, vue-router 는 그 이동을
        // **조용히 취소**한다. 화면도 안 바뀌고 에러도 안 뜬다 — 오래 열어둔
        // 탭에서 "링크를 눌러도 아무 일이 없는" 증상이 이것이다.
        // 미인증으로 확정하고 가드가 로그인으로 보내게 둔다.
        this.user = null;
      } finally {
        this.loading = false;
      }
    },

    async login(input: LoginInput): Promise<void> {
      this.loading = true;
      try {
        this.user = await authApi.login(input);
      } finally {
        this.loading = false;
      }
    },

    async logout(): Promise<void> {
      try {
        await authApi.logout();
      } finally {
        this.user = null;
      }
    },

    clearSession(): void {
      this.user = null;
    },
  },
});
